import React from 'react';
import { Sparkles, CheckCircle, XCircle } from 'lucide-react';
import { BatchAssessment } from '../../types/finding';
import { Spinner } from '../../design-system';

interface AssessmentProgressBannerProps {
  assessment: BatchAssessment;
  onComplete?: () => void;
}

const AssessmentProgressBanner: React.FC<AssessmentProgressBannerProps> = ({
  assessment,
}) => {
  const { status, total_findings, assessed_count, failed_count, error_message } = assessment;
  const progress = total_findings > 0 ? ((assessed_count + failed_count) / total_findings) * 100 : 0;
  const isActive = status === 'queued' || status === 'running';
  const isCompleted = status === 'completed';
  const isFailed = status === 'failed';

  const getStatusIcon = () => {
    if (isActive) {
      return <Spinner size={20} tone="current" className="text-purple-400" />;
    }
    if (isCompleted) {
      return <CheckCircle className="w-5 h-5 text-emerald-400" />;
    }
    if (isFailed) {
      return <XCircle className="w-5 h-5 text-red-400" />;
    }
    return <Sparkles className="w-5 h-5 text-purple-400" />;
  };

  const getStatusText = () => {
    if (status === 'queued') {
      return 'AI assessment queued...';
    }
    if (status === 'running') {
      return `Assessing ${assessed_count + failed_count} of ${total_findings} findings...`;
    }
    if (isCompleted) {
      return `Assessment complete: ${assessed_count} assessed${failed_count > 0 ? `, ${failed_count} failed` : ''}`;
    }
    if (isFailed) {
      return error_message || 'Assessment failed';
    }
    return 'Assessment in progress';
  };

  const getBannerStyles = () => {
    if (isCompleted) {
      return 'bg-emerald-500/10 border-emerald-500/30';
    }
    if (isFailed) {
      return 'bg-red-500/10 border-red-500/30';
    }
    return 'bg-gradient-to-r from-purple-500/10 to-indigo-500/10 border-purple-500/30';
  };

  return (
    <div
      className={`relative overflow-hidden rounded-xl border p-4 mb-6 ${getBannerStyles()}`}
    >
      {/* Animated background shimmer for active states */}
      {isActive && (
        <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/5 to-transparent" />
      )}

      <div className="relative flex items-center gap-4">
        {/* Icon with pulse animation */}
        <div className={`flex-shrink-0 ${isActive ? 'animate-pulse' : ''}`}>
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500/20 to-indigo-500/20 flex items-center justify-center">
            {getStatusIcon()}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-4 h-4 text-purple-400" />
            <span className="text-sm font-semibold text-ui-text-primary">
              AI Assessment
            </span>
            {isActive && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 animate-pulse">
                In Progress
              </span>
            )}
          </div>
          <p className="text-sm text-ui-text-secondary break-words max-h-24 overflow-y-auto">{getStatusText()}</p>

          {/* Progress bar */}
          {(status === 'running' || isCompleted) && total_findings > 0 && (
            <div className="mt-3">
              <div className="h-1.5 w-full bg-ui-background-tertiary rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ease-out ${
                    isCompleted
                      ? 'bg-emerald-500'
                      : 'bg-gradient-to-r from-purple-500 to-indigo-500'
                  }`}
                  style={{ width: `${Math.min(progress, 100)}%` }}
                />
              </div>
              <div className="flex justify-between mt-1.5 text-xs text-ui-text-tertiary">
                <span>{assessed_count + failed_count} of {total_findings}</span>
                <span>{Math.round(progress)}%</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssessmentProgressBanner;

